import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { FormBuilder, FormGroup } from "@angular/forms";
import { BehaviorSubject, combineLatest, of, Subscription } from "rxjs";
import {
    catchError,
    debounceTime,
    startWith,
    switchMap,
    take,
} from "rxjs/operators";
import { TableColumn } from "@/components/shared/table-header/table-header.component";
import { AssessmentDto, FormType } from "@/_models";
import { AssessmentService } from "@/_services";
import { RadioOption } from "@/components/shared/radio-list/radio-list.component";
import { AccountService } from "@/_services/account.service";
import { isEnabled } from "@features/functions";
import { CATALOG_OWNER } from "@/_utility/catalog-owner.util";
import { defaultIndustryFilter, INDUSTRY_OPTIONS } from "@/_models/industry";

type TemplateTab = "general" | "owned";

@Component({
    selector: "app-template-list",
    templateUrl: "./template-list.component.html",
    styleUrl: "./template-list.component.scss",
})
export class TemplateListComponent implements OnInit, OnDestroy {
    rows: AssessmentDto[] = [];
    loading = true;
    loadFailed = false;

    activeTab: TemplateTab = "owned";
    isOwnedTab = true;

    currentFilters: { [key: string]: unknown } | null = null;
    hasActiveFilters = false;
    currentSorting: { key: string; value: string } = {
        key: "title",
        value: "asc",
    };

    filterForm: FormGroup;

    formattedTypeList: RadioOption[] = [];
    formattedIndustryList: RadioOption[] = INDUSTRY_OPTIONS;
    formTypeList: FormType[] = [];

    readonly industryEnabled = isEnabled("formTemplateIndustry");

    columns: TableColumn[] = [
        { label: "Title", value: "title", isDisplayed: true },
        { label: "Type", value: "type", isDisplayed: true },
        ...(this.industryEnabled
            ? [{ label: "Industry", value: "industries", isDisplayed: true }]
            : []),
    ];

    private accountId: string | null = null;
    private accountName = "Your Templates";
    private accountIndustries: string[] = [];
    private ownerParam = CATALOG_OWNER;
    private sort$ = new BehaviorSubject(this.buildSortQuery());
    private routeSub?: Subscription;
    private loadSub?: Subscription;

    constructor(
        private accountService: AccountService,
        private formBuilder: FormBuilder,
        private assessmentService: AssessmentService,
        private route: ActivatedRoute,
    ) {}

    ngOnInit() {
        this.filterForm = this.formBuilder.group({
            title: [""],
            type: [""],
            ...(this.industryEnabled ? { industries: [[]] } : {}),
        });

        this.assessmentService.formTypeList().subscribe((formTypeList) => {
            this.formTypeList = formTypeList;
            this.formattedTypeList = formTypeList
                .filter((type) => type.selectable)
                .map((type) => ({ label: type.name, value: type.type }));
        });

        this.routeSub = this.route.queryParams.subscribe((params) => {
            const nextTab: TemplateTab =
                params["type"] === "general" ? "general" : "owned";
            const tabChanged = nextTab !== this.activeTab;
            this.activeTab = nextTab;
            this.isOwnedTab = nextTab === "owned";
            this.ownerParam = this.isOwnedTab
                ? this.accountId ?? CATALOG_OWNER
                : CATALOG_OWNER;

            if (tabChanged) {
                this.resetFiltersForTab();
            }
            if (this.accountId) {
                this.reload();
            }
        });

        this.accountService
            .getAccount()
            .pipe(
                take(1),
                catchError(() => of(null)),
            )
            .subscribe((account) => {
                this.accountId = account?._id ?? null;
                this.accountName = account?.name || "Your Templates";
                this.accountIndustries = account?.industries ?? [];
                this.ownerParam = this.isOwnedTab
                    ? this.accountId ?? CATALOG_OWNER
                    : CATALOG_OWNER;

                this.resetFiltersForTab();
                this.subscribeToTemplates();
            });
    }

    ngOnDestroy(): void {
        this.routeSub?.unsubscribe();
        this.loadSub?.unsubscribe();
    }

    setSorting(sort: { key: string; value: string }) {
        this.currentSorting = sort;
        this.sort$.next(this.buildSortQuery());
    }

    clearFilters() {
        this.filterForm.reset(this.emptyFilters());
    }

    reload(): void {
        this.sort$.next(this.buildSortQuery());
    }

    private emptyFilters() {
        return {
            title: "",
            type: "",
            ...(this.industryEnabled ? { industries: [] } : {}),
        };
    }

    private resetFiltersForTab(): void {
        const defaults = this.emptyFilters();
        if (this.industryEnabled && !this.isOwnedTab) {
            defaults.industries = defaultIndustryFilter(this.accountIndustries);
        }
        this.filterForm.reset(defaults, { emitEvent: false });
        this.currentFilters = { ...this.filterForm.value };
    }

    private subscribeToTemplates(): void {
        this.loadSub = combineLatest([
            this.filterForm.valueChanges.pipe(startWith(null)),
            this.sort$,
        ])
            .pipe(
                debounceTime(300),
                switchMap(([, sort]) => {
                    const value = this.filterForm.value;
                    this.currentFilters = { ...value };
                    this.hasActiveFilters = this.filtersAreActive();
                    this.loading = true;
                    this.loadFailed = false;
                    return this.assessmentService
                        .getFormList(
                            {
                                title: value.title || undefined,
                                type: value.type || undefined,
                                industries: value.industries?.length
                                    ? value.industries
                                    : undefined,
                                owner: this.ownerParam,
                            },
                            sort,
                        )
                        .pipe(
                            catchError(() => {
                                this.loadFailed = true;
                                return of({ data: [] as AssessmentDto[] });
                            }),
                        );
                }),
            )
            .subscribe((list) => {
                this.rows = list.data;
                this.loading = false;
            });
    }

    private filtersAreActive(): boolean {
        return Object.values(this.filterForm.value).some((value) =>
            Array.isArray(value) ? value.length > 0 : !!value,
        );
    }

    private buildSortQuery(): { [sortKey: string]: "asc" | "desc" } {
        const sorting = this.currentSorting ?? { key: "title", value: "asc" };
        return sorting.key
            ? { [sorting.key]: sorting.value as "asc" | "desc" }
            : {};
    }
}
